'use client';

import { c as _c } from "react/compiler-runtime";
import { Tab } from '@common/components';
import { useSearchParams } from 'next/navigation';
import { type CSSProperties, Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/_components/DonationLayout/DonationSubHeader';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { TABS, TABS_LABELS } from '../constants';
import type { Tab as TabType } from '../types';
interface GiftDetailTabProps {
  className?: string;
  style?: CSSProperties;
  productNumber: string;
  disabledTabs?: TabType[];
}
const MOBILE_STICKY_OFFSET = 48;
const DESKTOP_STICKY_OFFSET = DONATION_DESKTOP_STICKY_TOP;
const GiftDetailTabComponent = t0 => {
  const $ = _c(22);
  const {
    className,
    style,
    disabledTabs
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const {
    scrollToSectionWithTab
  } = useScrollToSection();
  const searchParams = useSearchParams();
  const [tab, setTab] = useState(TABS.BASIC);
  const isUserScrolling = useRef(true);
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams?.get("tab");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const requestedTab = t1 as TabType | null;
  let t2;
  let t3;
  if ($[2] !== isDesktopViewport || $[3] !== requestedTab || $[4] !== scrollToSectionWithTab) {
    t2 = () => {
      if (requestedTab) {
        scrollToSectionWithTab(requestedTab, {
          behavior: "instant",
          offset: isDesktopViewport ? DESKTOP_STICKY_OFFSET : MOBILE_STICKY_OFFSET
        });
      }
    };
    t3 = [isDesktopViewport, scrollToSectionWithTab, requestedTab];
    $[2] = isDesktopViewport;
    $[3] = requestedTab;
    $[4] = scrollToSectionWithTab;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[7] !== disabledTabs) {
    t4 = () => {
      const tabIds = Object.values(TABS).filter(id => !disabledTabs?.includes(id));
      const visibleIds = new Set();
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const entryId = tabIds.find(id_0 => id_0 === entry.target.id);
          if (!entryId) {
            continue;
          }
          if (entry.isIntersecting) {
            visibleIds.add(entryId);
          } else {
            visibleIds.delete(entryId);
          }
        }
        if (!isUserScrolling.current) {
          return;
        }
        const activeId = tabIds.filter(id_1 => visibleIds.has(id_1)).at(-1);
        if (activeId) {
          setTab(activeId);
        }
      }, {
        rootMargin: "0px 0px -50% 0px",
        threshold: 0
      });
      tabIds.forEach(id_2 => {
        const element = document.getElementById(id_2);
        if (element) {
          observer.observe(element);
        }
      });
      return () => observer.disconnect();
    };
    t5 = [disabledTabs];
    $[7] = disabledTabs;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  const t6 = isDesktopViewport ? "lg" : "sm";
  const t7 = tab ?? TABS.BASIC;
  let t8;
  if ($[10] !== isDesktopViewport || $[11] !== scrollToSectionWithTab) {
    t8 = value => {
      isUserScrolling.current = false;
      setTab(value);
      scrollToSectionWithTab(value, {
        offset: isDesktopViewport ? DESKTOP_STICKY_OFFSET : MOBILE_STICKY_OFFSET
      });
      setTimeout(() => {
        isUserScrolling.current = true;
      }, 1000);
    };
    $[10] = isDesktopViewport;
    $[11] = scrollToSectionWithTab;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== disabledTabs) {
    t9 = Object.values(TABS).filter(tab_0 => !disabledTabs?.includes(tab_0)).map(_temp);
    $[13] = disabledTabs;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== className || $[16] !== style || $[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = <Tab size={t6} className={className} style={style} variant="full" value={t7} onChange={t8}>{t9}</Tab>;
    $[15] = className;
    $[16] = style;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
};
export const GiftDetailTab = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-70 border-b border-Gray-300 mobile-tablet:h-53" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <ErrorBoundary fallback={null}><Suspense fallback={t0}><GiftDetailTabComponent {...props} /></Suspense></ErrorBoundary>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function _temp(tab_1) {
  return <Tab.Item key={tab_1} value={tab_1}>{TABS_LABELS[tab_1]}</Tab.Item>;
}